import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Play, Plus, Check, Star, ArrowLeft, SkipForward, MessageCircle, Trash2, Send, User } from "lucide-react";
import { toast } from "sonner";
import { api, videoEmbed, SITE_URL, formatApiError, personPath } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { Navbar } from "@/components/Navbar";
import { AdSlot } from "@/components/AdSlot";
import { SocialShareBar } from "@/components/SocialLinks";
import { StreamingPlatforms } from "@/components/StreamingPlatforms";

const isYouTube = (u) => u && /youtu\.?be/.test(u);

export default function TitleDetail() {
  const { slug } = useParams();
  const { user, checking } = useAuth();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [movie, setMovie] = useState(null);

  // Require registration/login to view any title page
  useEffect(() => {
    if (!checking && user === false) {
      toast.info("Regístrate o inicia sesión para ver cualquier contenido de La Caja LGBT");
      navigate("/auth", { replace: true, state: { from: `/titulo/${slug}` } });
    }
  }, [user, checking, navigate, slug]);
  const [media, setMedia] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [adVideo, setAdVideo] = useState(null);
  const [canSkip, setCanSkip] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [postingComment, setPostingComment] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/movies/slug/${slug}`, { params: { lang } });
      setMovie(data);
      setMedia(null);
      setPlaying(false);
      setAdVideo(null);
      window.scrollTo(0, 0);
    } catch {
      setNotFound(true);
    }
  }, [slug, lang]);

  useEffect(() => { load(); }, [load]);

  // Carga diferida de video + reparto (no bloquea el contenido)
  useEffect(() => {
    if (!movie?.id) return;
    let active = true;
    api.get(`/movies/${movie.id}/media`, { params: { lang } })
      .then(({ data }) => {
        if (!active) return;
        setMedia(data);
        setMovie((prev) => prev ? { ...prev, title: data.title || prev.title, description: data.description || prev.description } : prev);
      })
      .catch(() => {});
    api.get(`/movies/${movie.id}/comments`).then(({ data }) => { if (active) setComments(data); }).catch(() => {});
    return () => { active = false; };
  }, [movie?.id, lang]);

  // Temporizador para habilitar "saltar anuncio"
  useEffect(() => {
    if (!adVideo) return;
    setCanSkip(false);
    const s = adVideo.skip || 5;
    const timer = setTimeout(() => setCanSkip(true), s * 1000);
    return () => clearTimeout(timer);
  }, [adVideo]);

  const requireAuth = () => {
    if (!user) { navigate("/auth"); return false; }
    return true;
  };

  const startTrailer = () => {
    setAdVideo(null);
    setPlaying(true);
    api.post(`/progress/${movie.id}`, { position: 1, duration: 100 }).catch(() => {});
  };

  const play = async () => {
    if (!requireAuth()) return;
    if (playing || adVideo) return;
    try {
      const { data } = await api.get("/ads/preroll");
      if (data.video_url) {
        setAdVideo({ url: data.video_url, skip: data.skip_seconds || 5 });
      } else {
        startTrailer();
      }
    } catch {
      startTrailer();
    }
  };

  const toggleList = async () => {
    if (!requireAuth()) return;
    try {
      if (movie.in_list) await api.delete(`/mylist/${movie.id}`);
      else await api.post(`/mylist/${movie.id}`);
      setMovie({ ...movie, in_list: !movie.in_list });
      toast.success(movie.in_list ? t("removedFromList") : t("addedToList"));
    } catch { toast.error("Error"); }
  };

  const rate = async (v) => {
    if (!requireAuth()) return;
    try {
      const { data } = await api.post(`/ratings/${movie.id}`, { value: v });
      setMovie({ ...movie, ...data, in_list: movie.in_list });
      toast.success(t("thanksRating"));
    } catch { toast.error("Error"); }
  };

  const postComment = async (e) => {
    e.preventDefault();
    if (!requireAuth()) return;
    const text = commentText.trim();
    if (!text) return;
    setPostingComment(true);
    try {
      const { data } = await api.post(`/movies/${movie.id}/comments`, { text });
      setComments((prev) => [data, ...prev]);
      setCommentText("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setPostingComment(false);
    }
  };

  const deleteComment = async (id) => {
    try {
      await api.delete(`/comments/${id}`);
      setComments((prev) => prev.filter((c) => c.id !== id));
    } catch { toast.error("Error"); }
  };

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#050505]"><Navbar />
        <div className="pt-32 text-center text-zinc-400">
          <p>{t("titleNotFound")}</p>
          <Link to="/" className="text-[#E11D48] mt-4 inline-block">{t("back")}</Link>
        </div>
      </div>
    );
  }
  if (!movie) {
    return <div className="min-h-screen flex items-center justify-center bg-[#050505]"><div className="w-10 h-10 border-2 border-[#E11D48] border-t-transparent rounded-full animate-spin" /></div>;
  }

  const trailerUrl = media?.trailer_url || movie.trailer_url;
  const embed = videoEmbed(trailerUrl);
  const rawCast = (media?.credits?.cast) || (movie.credits && movie.credits.cast) || movie.cast || [];
  let cast = [];
  if (Array.isArray(rawCast)) {
    cast = rawCast.map((item, idx) => {
      if (typeof item === "string") {
        return { id: `cast-s-${idx}`, name: item, profile_url: null, character: null };
      }
      return item;
    });
  } else if (typeof rawCast === "string" && rawCast.trim()) {
    cast = rawCast.split(",").map((s, idx) => ({ id: `cast-s-${idx}`, name: s.trim(), profile_url: null, character: null })).filter((x) => x.name);
  }
  const directors = (media?.credits?.directors) || (movie.credits && movie.credits.directors) || [];
  const url = `${SITE_URL}/titulo/${movie.slug}`;

  const rawImage = movie.backdrop_url || movie.poster_url;
  const absoluteOgImage = rawImage
    ? (rawImage.startsWith("http") ? rawImage : `${SITE_URL}${rawImage.startsWith("/") ? "" : "/"}${rawImage}`)
    : `${SITE_URL}/og-image.png`;

  const metaDescriptionText = (movie.description || `Ver ${movie.title} online gratis en La Caja LGBT.`).slice(0, 200);
  const pageTitleText = `${movie.title}${movie.year ? ` (${movie.year})` : ""} — La Caja LGBT`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": movie.media_type === "tv" ? "TVSeries" : "Movie",
    name: movie.title,
    description: movie.description,
    image: absoluteOgImage,
    datePublished: movie.year ? `${movie.year}-01-01` : undefined,
    genre: movie.genres,
    director: directors.map((d) => ({ "@type": "Person", name: d.name })),
    actor: (Array.isArray(cast) ? cast : []).slice(0, 8).map((a) => ({ "@type": "Person", name: a.name })),
    aggregateRating: movie.rating_count > 0 ? { "@type": "AggregateRating", ratingValue: movie.avg_rating, ratingCount: movie.rating_count, bestRating: 5 } : undefined,
  };

  return (
    <div className="min-h-screen bg-[#050505] pb-20" data-testid="title-detail">
      <Helmet>
        <title>{pageTitleText}</title>
        <meta name="description" content={metaDescriptionText} />
        <link rel="canonical" href={url} />
        <link rel="alternate" hrefLang="es" href={`${url}?lang=es`} />
        <link rel="alternate" hrefLang="en" href={`${url}?lang=en`} />
        <link rel="alternate" hrefLang="fr" href={`${url}?lang=fr`} />
        <link rel="alternate" hrefLang="x-default" href={url} />

        {/* Specific Open Graph Meta Tags for Individual Movie Pages */}
        <meta property="og:site_name" content="La Caja LGBT" />
        <meta property="og:title" content={pageTitleText} />
        <meta property="og:description" content={metaDescriptionText} />
        <meta property="og:type" content={movie.media_type === "tv" ? "video.tv_show" : "video.movie"} />
        <meta property="og:url" content={url} />
        <meta property="og:image" content={absoluteOgImage} />
        <meta property="og:image:secure_url" content={absoluteOgImage} />
        <meta property="og:image:alt" content={movie.title} />
        <meta property="og:locale" content="es_ES" />
        {movie.year && <meta property="video:release_date" content={`${movie.year}-01-01`} />}
        {movie.duration && <meta property="video:duration" content={String(movie.duration)} />}
        {directors.map((d, i) => (
          <meta key={`dir-${i}`} property="video:director" content={d.name} />
        ))}
        {Array.isArray(movie.genres) && movie.genres.map((g, i) => (
          <meta key={`gen-${i}`} property="video:tag" content={g} />
        ))}

        {/* Specific Twitter Meta Tags for Individual Movie Pages */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@lacajalgbt" />
        <meta name="twitter:creator" content="@lacajalgbt" />
        <meta name="twitter:title" content={pageTitleText} />
        <meta name="twitter:description" content={metaDescriptionText} />
        <meta name="twitter:image" content={absoluteOgImage} />
        <meta name="twitter:image:alt" content={movie.title} />

        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>
      <Navbar />

      <div className="relative h-[60vh] md:h-[72vh] bg-black">
        {playing && embed ? (
          <iframe src={embed} title={movie.title} className="w-full h-full" allow="autoplay; encrypted-media" allowFullScreen data-testid="trailer-iframe" />
        ) : adVideo ? (
          <div className="w-full h-full relative bg-black" data-testid="preroll-ad">
            <span className="absolute top-3 left-3 z-20 text-[10px] tracking-[0.2em] uppercase text-white bg-black/60 border border-white/20 px-2 py-1">{t("adLabel")}</span>
            {isYouTube(adVideo.url) ? (
              <iframe src={videoEmbed(adVideo.url)} title="Anuncio" className="w-full h-full" allow="autoplay; encrypted-media" allowFullScreen />
            ) : (
              <video src={adVideo.url} autoPlay controls={false} onEnded={startTrailer} className="w-full h-full object-contain" />
            )}
            <button
              data-testid="skip-ad-button"
              onClick={startTrailer}
              disabled={!canSkip}
              className="absolute bottom-4 right-4 z-20 flex items-center gap-2 px-4 py-2 text-sm bg-black/80 border border-white/30 text-white disabled:opacity-40 hover:bg-black"
            >
              <SkipForward size={16} /> {canSkip ? "Saltar anuncio" : `Anuncio (${adVideo.skip}s)`}
            </button>
          </div>
        ) : (
          <>
            {(movie.backdrop_url || movie.poster_url) ? (
              <img
                src={movie.backdrop_url || movie.poster_url}
                alt={movie.title}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = "https://images.unsplash.com/photo-1518709268805-4e9042af9f23?w=1280&auto=format&fit=crop&q=80";
                }}
              />
            ) : <div className="w-full h-full bg-zinc-900" />}
            <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/50 to-transparent" />
            <button onClick={play} data-testid="detail-play-overlay" className="absolute inset-0 m-auto w-20 h-20 rounded-full bg-white text-black flex items-center justify-center hover:scale-105 transition-transform">
              <Play size={30} className="fill-black ml-1" />
            </button>
          </>
        )}
      </div>

      <div className="px-6 md:px-16 -mt-20 relative z-10">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-zinc-400 hover:text-white text-sm mb-4" data-testid="detail-back">
          <ArrowLeft size={16} /> {t("back")}
        </button>
        <div className="flex flex-col md:flex-row gap-8">
          {movie.poster_url ? (
            <img
              src={movie.poster_url}
              alt={movie.title}
              className="w-40 md:w-56 aspect-[2/3] object-cover rounded-sm border border-white/10 shrink-0"
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = "https://images.unsplash.com/photo-1485846234645-a62644f84728?w=500&auto=format&fit=crop&q=80";
              }}
            />
          ) : (
            <div className="w-40 md:w-56 aspect-[2/3] rounded-sm border border-white/10 shrink-0 bg-zinc-900 flex items-center justify-center text-zinc-600 text-xs text-center p-3">{movie.title}</div>
          )}
          <div className="flex-1">
            <h1 className="font-heading text-3xl md:text-5xl font-light tracking-tighter flex flex-wrap items-center gap-3">
              <span>{movie.title}</span>
              {(movie.custom_badge || movie.upcoming) && (
                <span className="text-xs font-bold uppercase tracking-wider bg-gradient-to-r from-amber-500 to-rose-500 text-white px-3 py-1 rounded-full shadow-lg border border-amber-300/30">
                  {movie.custom_badge || t("upcoming")}
                </span>
              )}
            </h1>
            <div className="flex flex-wrap items-center gap-3 text-sm text-zinc-400 mt-3">
              {movie.year && <span>{movie.year}</span>}
              <span className="uppercase text-xs border border-white/15 px-2 py-0.5">{movie.media_type === "tv" ? t("series") : t("movie")}</span>
              {movie.release_date && (
                <span className="text-xs text-amber-400 bg-amber-400/10 px-2.5 py-0.5 rounded border border-amber-400/20 font-medium">
                  {t("releaseDate")}: {movie.release_date}
                </span>
              )}
              {movie.avg_rating > 0 && <span className="flex items-center gap-1"><Star size={14} className="fill-[#E11D48] text-[#E11D48]" /> {movie.avg_rating} ({movie.rating_count})</span>}
              {movie.genres?.map((g) => <span key={g} className="text-zinc-500">· {g}</span>)}
            </div>

            <div className="flex flex-wrap items-center gap-3 mt-6">
              <button data-testid="detail-play-button" onClick={play} className="bg-[#E11D48] text-white px-7 py-3 font-medium hover:bg-[#BE123C] transition-colors flex items-center gap-2">
                <Play size={18} className="fill-white" /> {movie.upcoming ? "Ver Tráiler / Avance" : t("watchMovie")}
              </button>
              <button data-testid="detail-mylist-button" onClick={toggleList} className="w-12 h-12 border border-white/20 flex items-center justify-center hover:bg-white/10 transition-colors">
                {movie.in_list ? <Check size={18} className="text-[#E11D48]" /> : <Plus size={18} />}
              </button>

              {/* Botones de plataformas de streaming oficiales al lado del botón principal */}
              <StreamingPlatforms movie={movie} variant="inline" />
            </div>

            <p className="text-zinc-300 leading-relaxed mt-6 max-w-3xl">{movie.description || t("synopsisNA")}</p>

            {directors.length > 0 && (
              <p className="text-sm mt-4"><span className="text-zinc-500">{t("direction")}: </span>
                {directors.map((d, i) => (
                  <Link key={d.id} to={personPath(d.id, d.name)} className="hover:text-[#E11D48]">{d.name}{i < directors.length - 1 ? ", " : ""}</Link>
                ))}
              </p>
            )}

            {/* Barra de Compartir en Redes Sociales */}
            <div className="mt-6 max-w-3xl">
              <SocialShareBar title={pageTitleText} description={metaDescriptionText} url={url} />
            </div>

            {/* Aviso de Transparencia Legal y Enlace DMCA de la Ficha */}
            <div className="mt-6 max-w-3xl pt-4 border-t border-white/5 flex flex-wrap items-center justify-between gap-3 text-[11px] text-zinc-500">
              <div className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500/80 shrink-0" />
                <span>Directorio informativo y de divulgación LGBTQ+. No alojamos archivos en servidores propios.</span>
              </div>
              <div className="flex items-center gap-3">
                <Link to="/legal/derechos" className="hover:text-zinc-300 transition-colors">
                  Aviso Legal DMCA
                </Link>
                <span className="text-zinc-700">·</span>
                <Link
                  to={`/legal/formulario-dmca?title=${encodeURIComponent(movie.title)}&url=${encodeURIComponent(url)}`}
                  className="text-amber-500/90 hover:text-amber-400 font-medium transition-colors hover:underline"
                >
                  ¿Titular de derechos? Notificar retirada
                </Link>
              </div>
            </div>

            {/* SECCIÓN DE REPARTO / ACTORES EN FORMATO REDONDO */}
            {cast.length > 0 && (
              <div className="mt-12 border-t border-white/10 pt-8 max-w-3xl" data-testid="cast-section">
                <h3 className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-400 mb-5 flex items-center gap-2">
                  <User size={14} className="text-[#E11D48]" /> {t("cast")}
                </h3>
                <div className="flex gap-6 overflow-x-auto no-scrollbar pb-4 pt-1">
                  {cast.map((p) => {
                    const isLink = p.id && !String(p.id).startsWith("cast-");
                    const profileImg = p.profile_url
                      ? (p.profile_url.startsWith("http") || p.profile_url.startsWith("/") ? p.profile_url : `https://image.tmdb.org/t/p/w185${p.profile_url.startsWith("/") ? "" : "/"}${p.profile_url}`)
                      : (p.profile_path ? `https://image.tmdb.org/t/p/w185${p.profile_path.startsWith("/") ? "" : "/"}${p.profile_path}` : null);

                    const Content = (
                      <div className="flex flex-col items-center text-center group cursor-pointer">
                        <div className="w-20 h-20 md:w-24 md:h-24 rounded-full overflow-hidden bg-zinc-900 border-2 border-white/15 group-hover:border-[#E11D48] transition-all shadow-lg shrink-0 flex items-center justify-center relative">
                          {profileImg ? (
                            <img
                              src={profileImg}
                              alt={p.name}
                              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                              loading="lazy"
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                                const fallback = e.currentTarget.parentElement?.querySelector(".avatar-fallback");
                                if (fallback) fallback.style.display = "flex";
                              }}
                            />
                          ) : null}
                          <div
                            className="avatar-fallback w-full h-full flex items-center justify-center bg-gradient-to-br from-zinc-800 to-zinc-950 text-zinc-300 font-bold text-sm md:text-base uppercase"
                            style={{ display: profileImg ? "none" : "flex" }}
                          >
                            {p.name ? p.name.split(" ").map(n => n[0]).join("").slice(0, 2) : "?"}
                          </div>
                        </div>
                        <p className="text-xs md:text-sm font-semibold text-white mt-2.5 truncate max-w-[96px] group-hover:text-[#E11D48] transition-colors">
                          {p.name}
                        </p>
                        {p.character && (
                          <p className="text-[11px] text-zinc-400 truncate max-w-[96px] mt-0.5 font-normal">
                            {p.character}
                          </p>
                        )}
                      </div>
                    );

                    return isLink ? (
                      <Link key={p.id || p.name} to={personPath(p.id, p.name)} data-testid={`cast-${p.id}`} className="flex-shrink-0">
                        {Content}
                      </Link>
                    ) : (
                      <div key={p.id || p.name} className="flex-shrink-0">
                        {Content}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="mt-10 pt-6 border-t border-white/10 max-w-3xl">
              <p className="text-xs tracking-[0.2em] uppercase text-zinc-400 mb-3">{t("yourRating")}</p>
              <div className="flex items-center gap-1.5" data-testid="rating-stars">
                {[1, 2, 3, 4, 5].map((s) => (
                  <button key={s} data-testid={`rate-star-${s}`} onClick={() => rate(s)} className="transition-transform hover:scale-110">
                    <Star size={26} className={s <= (movie.user_rating || 0) ? "fill-[#E11D48] text-[#E11D48]" : "text-zinc-600"} />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="my-16"><AdSlot slot="9813456583" /></div>

        <div className="mt-16 max-w-3xl" data-testid="comments-section">
          <h2 className="font-heading text-2xl mb-5 flex items-center gap-2">
            <MessageCircle size={22} className="text-[#E11D48]" /> {t("comments")} <span className="text-zinc-500 text-lg">({comments.length})</span>
          </h2>

          {user ? (
            <form onSubmit={postComment} className="flex items-start gap-3 mb-8" data-testid="comment-form">
              <div className="w-9 h-9 rounded-full bg-[#E11D48]/20 flex items-center justify-center text-sm uppercase shrink-0">
                {(user.name || user.email)[0]}
              </div>
              <div className="flex-1">
                <textarea
                  data-testid="comment-input"
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  rows={2}
                  maxLength={1000}
                  placeholder={t("writeCommentPlaceholder")}
                  className="w-full bg-transparent border border-white/15 p-3 outline-none focus:border-[#E11D48] resize-none text-sm"
                />
                <button
                  data-testid="comment-submit"
                  type="submit"
                  disabled={postingComment || !commentText.trim()}
                  className="mt-2 bg-[#E11D48] text-white px-5 py-2 text-sm font-medium hover:bg-[#BE123C] transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                  <Send size={15} /> {postingComment ? t("posting") : t("publish")}
                </button>
              </div>
            </form>
          ) : (
            <p className="text-sm text-zinc-400 mb-8">
              <Link to="/auth" className="text-[#E11D48] hover:underline" data-testid="comment-login-link">{t("loginToComment")}</Link>
            </p>
          )}

          {comments.length === 0 ? (
            <p className="text-sm text-zinc-500" data-testid="comments-empty">{t("noComments")}</p>
          ) : (
            <div className="space-y-5">
              {comments.map((c) => (
                <div key={c.id} className="flex items-start gap-3 group" data-testid={`comment-${c.id}`}>
                  <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center text-sm uppercase shrink-0">
                    {(c.user_name || "?")[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{c.user_name}</span>
                      <span className="text-xs text-zinc-600">{new Date(c.created_at).toLocaleDateString()}</span>
                    </div>
                    {/* URLs no clicables: se renderiza como texto plano para no indexar enlaces */}
                    <p className="text-sm text-zinc-300 mt-1 whitespace-pre-wrap break-words">{c.text}</p>
                  </div>
                  {user && (c.user_id === user.user_id || user.role === "admin") && (
                    <button
                      data-testid={`comment-delete-${c.id}`}
                      onClick={() => deleteComment(c.id)}
                      className="text-zinc-600 hover:text-[#E11D48] p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Trash2 size={15} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
